import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useConfiguratorStore from '../store/configuratorStore';

const Container = styled.div`
  margin-bottom: 2rem;
`;

const FiltersBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FilterLabel = styled.div`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-700);
`;

const SortSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: var(--rounded-md);
  border: 1px solid var(--gray-300);
  background-color: white;
  font-size: var(--text-sm);
  color: var(--gray-800);
  min-width: 150px;
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: var(--rounded-md);
  border: 1px solid var(--gray-300);
  background-color: white;
  font-size: var(--text-sm);
  color: var(--gray-800);
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 112, 243, 0.1);
  }
`;

const ClearFilters = styled.button`
  font-size: var(--text-sm);
  color: var(--primary-color);
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.div)`
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-200)'};
  border-radius: var(--rounded-lg);
  overflow: hidden;
  transition: var(--transition-fast);
  cursor: pointer;
  background-color: white;
  box-shadow: var(--shadow-xs);
  position: relative;
  
  &:hover {
    border-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--primary-light)'};
    box-shadow: var(--shadow-md);
    transform: translateY(-4px);
  }
`;

const SelectedBadge = styled(motion.div)`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: var(--primary-color);
  color: white;
  border-radius: var(--rounded-full);
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  box-shadow: var(--shadow-sm);
  z-index: 2;
`;

const ProductImage = styled.div`
  height: 140px;
  background-color: ${props => props.selected ? 'var(--primary-light)' : 'var(--gray-100)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  border-bottom: 1px solid var(--gray-200);
  transition: var(--transition-fast);
  color: ${props => props.selected ? 'var(--primary-color)' : 'var(--secondary-color)'};
  position: relative;
  overflow: hidden;
  
  img {
    max-width: 85%;
    max-height: 85%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductContent = styled.div`
  padding: 1.5rem;
`;

const ProductHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ProductInfo = styled.div`
  flex: 1;
  padding-right: 1rem;
`;

const ProductName = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-900)'};
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: var(--transition-fast);
`;

const ProductCode = styled.div`
  font-size: var(--text-sm);
  color: var(--gray-500);
`;

const ProductPrice = styled.div`
  font-size: var(--text-xl);
  font-weight: 700;
  color: ${props => props.selected ? 'var(--primary-color)' : 'var(--gray-900)'};
  white-space: nowrap;
  transition: var(--transition-fast);
`;

const AttributesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const AttributeTag = styled.span`
  font-size: var(--text-xs);
  color: var(--gray-600);
  background-color: var(--gray-100);
  border-radius: var(--rounded-sm);
  padding: 0.25rem 0.5rem;
`;

const ViewButton = styled(motion.button)`
  width: 100%;
  background-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--primary-light)'};
  color: ${props => props.selected ? 'white' : 'var(--primary-color)'};
  font-weight: 600;
  font-size: var(--text-sm);
  padding: 0.75rem;
  border-radius: var(--rounded-md);
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: ${props => props.selected ? 'var(--primary-hover)' : 'var(--primary-color)'};
    color: white;
  }
`;

const EmptyState = styled.div`
  padding: 3rem;
  text-align: center;
  background-color: var(--gray-50);
  border-radius: var(--rounded-lg);
  color: var(--gray-600);
`;

const QuickNav = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const QuickNavButton = styled.button`
  white-space: nowrap;
  padding: 0.5rem 0.75rem;
  border-radius: var(--rounded-md);
  font-size: var(--text-sm);
  font-weight: 500;
  background-color: ${props => props.active ? 'var(--primary-light)' : 'var(--gray-100)'};
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--gray-700)'};
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : 'var(--gray-200)'};
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-light)' : 'var(--gray-200)'};
  }
`;

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

// Helper function to get product icon based on category
const getProductIcon = (mainCategoryId) => {
  const icons = {
    poles: '🏗️',
    decks: '🏢',
    unloading_stations: '🏭',
    big_bags: '👜'
  };
  
  return icons[mainCategoryId] || '📦';
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
};

function ProductList() {
  const { 
    selectedMainCategory,
    selectedProduct,
    selectedAttributes,
    getFilteredProducts,
    getProductsByAttributes,
    setSelectedProduct,
    calculateQuote
  } = useConfiguratorStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name');
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Get products based on current selections and filters
  useEffect(() => {
    let products = [];
    
    // Use attribute-matching products if attributes are selected
    if (Object.keys(selectedAttributes).length > 0) {
      products = getProductsByAttributes();
    } else {
      products = getFilteredProducts();
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(product => 
        product.name.toLowerCase().includes(query) || 
        product.id.toString().includes(query) ||
        (product.category && product.category.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    const sortedProducts = [...products].sort((a, b) => {
      // Special sorting for Decks
      if (selectedMainCategory === 'decks') {
        // Custom order for Neva Decks
        const getCustomOrder = (product) => {
          // First group: Standard versions
          if (product.name === 'Neva Deck | Standard') {
            return 1; // Standard comes first
          } 
          // Second group: Food Grade versions
          else if (product.name === 'Neva Deck | Food Grade') {
            return 2; // Food Grade comes second
          } 
          // Third group: Standard with open sides
          else if (product.name.includes('Neva Deck | Standard') && product.name.includes('open sides')) {
            return 3; // Standard with open sides comes third
          } 
          // Fourth group: Food Grade with open sides
          else if (product.name.includes('Neva Deck | Food Grade') && product.name.includes('open sides')) {
            return 4; // Food Grade with open sides comes fourth
          }
          // Fifth group: Standard No Slide
          else if (product.name.includes('Neva Deck | Standard') && product.name.includes('No Slide')) {
            return 5;
          }
          // Sixth group: Food Grade No Slide
          else if (product.name.includes('Neva Deck | Food Grade') && product.name.includes('No Slide')) {
            return 6;
          }
          // Seventh group: Stainless Steel versions
          else if (product.name.includes('Stainless Steel')) {
            return 7;
          }
          // All other products come after
          else {
            return 10;
          }
        };
        
        const orderA = getCustomOrder(a);
        const orderB = getCustomOrder(b);
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
      }
      
      // If not custom sorting, or if ties in custom sorting, apply selected sort
      switch (sortOption) {
        case 'price_asc':
          return a.basePrice - b.basePrice;
        case 'price_desc':
          return b.basePrice - a.basePrice;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'id':
          return a.id.localeCompare(b.id);
        default:
          return 0;
      }
    });
    
    setFilteredProducts(sortedProducts);
  }, [
    selectedMainCategory, 
    searchQuery, 
    sortOption, 
    selectedAttributes,
    getFilteredProducts,
    getProductsByAttributes
  ]);
  
  // Handle product selection  
  const handleProductClick = (productId) => {
    setSelectedProduct(productId);
    // Auto-calculate quote on selection
    setTimeout(() => calculateQuote(), 0);
  };
  
  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSortOption('name');
  };
  
  if (!selectedMainCategory) {
    return null;
  }
  
  if (filteredProducts.length === 0) {
    return (
      <EmptyState>
        <h3>No products found</h3>
        <p>Try selecting a different category or adjusting your search</p>
      </EmptyState>
    );
  }
  
  return (
    <Container>
      <FiltersBar>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <SortSelect 
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="name">Name</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="id">Article ID</option>
          </SortSelect>
        </FilterGroup>
        
        <FilterGroup>
          <SearchInput
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          {(searchQuery || sortOption !== 'name') && (
            <ClearFilters onClick={clearFilters}>
              Clear filters
            </ClearFilters>
          )}
        </FilterGroup>
      </FiltersBar>
      
      <AnimatePresence>
        <ProductGrid
          as={motion.div}
          key="products-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredProducts.map(product => {
            const isSelected = selectedProduct === product.id;
            
            return (
              <ProductCard
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                selected={isSelected}
                variants={itemVariants}
                whileTap={{ scale: 0.98 }}
              >
                {isSelected && (
                  <SelectedBadge
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring' }}
                  >
                    ✓
                  </SelectedBadge>
                )}
              
                <ProductImage selected={isSelected}>
                  {selectedMainCategory === 'decks' && product.name === 'Neva Deck | Standard' ? (
                    <img src="https://i.imgur.com/fUHm6LO.png" alt="Neva Deck Standard" />
                  ) : (
                    getProductIcon(selectedMainCategory)
                  )}
                </ProductImage>
                <ProductContent>
                  <ProductHeader>
                    <ProductInfo>
                      <ProductName selected={isSelected}>{product.name}</ProductName>
                      <ProductCode>Item #{product.id}</ProductCode>
                    </ProductInfo>
                    <ProductPrice selected={isSelected}>
                      {formatCurrency(product.basePrice)}
                    </ProductPrice>
                  </ProductHeader>
                  
                  <AttributesList>
                    {Object.entries(product.attributes || {}).map(([key, value], index) => (
                      <AttributeTag key={index}>
                        {value}
                      </AttributeTag>
                    ))}
                  </AttributesList>
                  
                  <ViewButton 
                    whileTap={{ scale: 0.97 }}
                    selected={isSelected}
                  >
                    {isSelected ? (
                      <>
                        <span>✓</span> Selected
                      </>
                    ) : (
                      'Configure Options'
                    )}
                  </ViewButton>
                </ProductContent>
              </ProductCard>
            );
          })}
        </ProductGrid>
      </AnimatePresence>
    </Container>
  );
}

export default ProductList;